import React, { useEffect, useState } from "react";
import axios from "utils/axiosConfig";
import { useSelector, useDispatch } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
} from "react-icons/hi";
import { useToast } from "../../components/Toast";
import { getProducts } from "../../features/products/productSlice";

import SidePanel from "../../components/SidePanel";

const ProductManagement = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const products = useSelector((state) => state.products.products?.data || []);
  const dispatch = useDispatch();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    category: "",
    image: "",
    description: "",
  });

  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      category: "",
      image: "",
      description: "",
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || "",
      price: product.price || "",
      category: product.category || "",
      image: product.image || "",
      description: product.description || "",
    });
    setIsPanelOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { "x-user-email": user?.data?.email } };
      if (editingProduct) {
        await axios.put(
          `${uri}/api/admin/products/${editingProduct._id}`,
          formData,
          config
        );
        toast.success("Catalog entry synchronized");
      } else {
        await axios.post(`${uri}/api/admin/products`, formData, config);
        toast.success("New module deployed successfully");
      }
      setIsPanelOpen(false);
      resetForm();
      dispatch(getProducts());
    } catch (error) {
      toast.error("Deployment failed: Protocol error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanent decommission this hardware module?")) return;
    try {
      await axios.delete(`${uri}/api/admin/products/${id}`, {
        headers: { "x-user-email": user?.data?.email },
      });
      toast.success("Module decommissioned");
      dispatch(getProducts());
    } catch (error) {
      toast.error("Authorization required for decommission");
    }
  };

  const categories = ["PCs", "Consoles", "Controllers", "Headphones"];

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 relative">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              Inventory_Status: Online
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Hardware <span className="text-cyan-400">Inventory</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Deploy and manage high-performance gaming components.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsPanelOpen(true);
            }}
            className="group relative flex items-center justify-center gap-3 px-8 py-5 bg-cyan-500 text-black font-black rounded-2xl hover:bg-cyan-400 transition-all shadow-[0_0_25px_rgba(0,242,255,0.2)] transform active:scale-95"
          >
            <HiOutlinePlus
              size={22}
              className="group-hover:rotate-90 transition-transform duration-300"
            />
            <span className="uppercase tracking-widest italic text-xs">
              Deploy New Module
            </span>
          </button>
        </header>

        {/* Search & Intelligence */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] blur opacity-10 group-focus-within:opacity-30 transition-opacity" />
          <div className="relative flex items-center bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-[2rem] p-2">
            <div className="pl-6 pr-4 text-cyan-500">
              <HiOutlineSearch size={22} />
            </div>
            <input
              type="text"
              placeholder="Search hardware by serial name or classification..."
              className="flex-1 bg-transparent border-none py-4 text-white placeholder:text-gray-600 font-black italic tracking-tighter focus:ring-0 outline-none text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="px-6 border-l border-white/5 flex items-center gap-2">
              <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                SKU_Count:
              </span>
              <span className="text-sm font-black text-cyan-400 italic shadow-[0_0_10px_#00f2ff]">
                {products.length}
              </span>
            </div>
          </div>
        </div>

        {/* Table Module */}
        <div className="bg-[#111827]/40 backdrop-blur-2xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02] border-b border-white/5 text-gray-500 text-[10px] font-black tracking-[0.3em] uppercase italic">
                  <th className="px-10 py-8">Hardware Specification</th>
                  <th className="px-10 py-8">Classification</th>
                  <th className="px-10 py-8">Valuation</th>
                  <th className="px-10 py-8 text-right">Operations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-xs font-bold">
                {products
                  .filter((p) =>
                    p.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((product) => (
                    <tr
                      key={product._id}
                      className="hover:bg-cyan-500/[0.03] transition-all duration-300 group"
                    >
                      <td className="px-10 py-8 flex items-center gap-6">
                        <div className="h-16 w-16 rounded-2xl bg-white/5 border border-white/10 p-1 group-hover:scale-110 transition-transform flex items-center justify-center overflow-hidden">
                          <img
                            src={product.image}
                            alt=""
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div>
                          <p className="text-white text-base font-black italic tracking-tight">
                            {product.name}
                          </p>
                          <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mt-1">
                            CODE_{product._id.slice(-6).toUpperCase()}
                          </p>
                        </div>
                      </td>
                      <td className="px-10 py-8">
                        <span className="px-4 py-2 bg-white/5 rounded-xl text-gray-400 text-[10px] font-black uppercase tracking-widest border border-white/5 group-hover:border-purple-500/30 transition-colors">
                          {product.category}
                        </span>
                      </td>
                      <td className="px-10 py-8">
                        <span className="text-lg font-black text-white tracking-tighter italic">
                          ${product.price}
                        </span>
                      </td>
                      <td className="px-10 py-8">
                        <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500">
                          <button
                            onClick={() => handleEdit(product)}
                            className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-cyan-400 rounded-2xl transition-all hover:shadow-[0_0_15px_rgba(0,242,255,0.2)]"
                          >
                            <HiOutlinePencil size={20} />
                          </button>
                          <button
                            onClick={() => handleDelete(product._id)}
                            className="w-12 h-12 flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 rounded-2xl transition-all hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                          >
                            <HiOutlineTrash size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {products.length === 0 && (
            <div className="py-24 text-center">
              <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic text-xs">
                No hardware indexed in the local matrix.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Deploy Module SidePanel */}
      <SidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        title={
          editingProduct ? "Alter Hardware Entry" : "Initialize New Module"
        }
      >
        <form onSubmit={handleSubmit} className="space-y-10">
          <div className="grid grid-cols-1 gap-10">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                Serial_Label
              </label>
              <input
                type="text"
                required
                className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-cyan-500/50 transition-all outline-none font-black text-white italic placeholder:text-gray-700"
                placeholder="Ex. Quantum RGB Blade v2"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                  Classification
                </label>
                <select
                  className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-cyan-500/50 transition-all outline-none font-black text-white italic cursor-pointer appearance-none"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <option value="" className="bg-[#0b0e14]">
                    Select Class
                  </option>
                  {categories.map((c) => (
                    <option key={c} value={c} className="bg-[#0b0e14]">
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                  Valuation ($)
                </label>
                <input
                  type="number"
                  required
                  className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-cyan-500/50 transition-all outline-none font-black text-white italic"
                  placeholder="299"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                Visualization_URL
              </label>
              <input
                type="text"
                required
                className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-cyan-500/50 transition-all outline-none font-black text-white italic placeholder:text-gray-700"
                placeholder="https://images.unsplash.com/..."
                value={formData.image}
                onChange={(e) =>
                  setFormData({ ...formData, image: e.target.value })
                }
              />
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                Tactical_Intelligence
              </label>
              <textarea
                required
                rows={4}
                className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-cyan-500/50 transition-all outline-none font-black text-white italic placeholder:text-gray-700 resize-none"
                placeholder="Identify key specifications and deployment capabilities..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex gap-6 pt-6">
            <button
              type="button"
              onClick={() => setIsPanelOpen(false)}
              className="flex-1 py-6 bg-white/5 text-gray-400 font-black rounded-2xl hover:bg-white/10 transition-all transform active:scale-95 uppercase tracking-widest text-[10px] italic border border-white/5"
            >
              Abort Protocol
            </button>
            <button
              type="submit"
              className="flex-1 py-6 bg-cyan-500 text-black font-black rounded-2xl hover:bg-cyan-400 shadow-[0_0_20px_rgba(0,242,255,0.2)] transition-all transform active:scale-95 uppercase tracking-widest text-[10px] italic"
            >
              {editingProduct ? "Synchronize Entry" : "Commit Deployment"}
            </button>
          </div>
        </form>
      </SidePanel>
    </AdminLayout>
  );
};

export default ProductManagement;
